import { CSSObject, Theme, keyframes } from '@emotion/react';
import { mobile } from 'styles/media';

const popIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: none; }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: none; }
`;

const wrap =
  (size: 'small' | 'medium') =>
  (theme: Theme): CSSObject => ({
    position: 'relative',
    maxWidth: 'min(80vw, 1200px)',
    minWidth: 360,
    maxHeight: '85vh',
    boxSizing: 'border-box',
    background: theme.color.white,
    padding: size === 'medium' ? '28px' : '24px',
    borderRadius: theme.radius.xl + 4,
    boxShadow: theme.shadow.modal,
    display: 'flex',
    flexDirection: 'column',
    animation: `${popIn} 0.2s ease-out`,
    [mobile]: {
      alignSelf: 'flex-end',
      width: 'calc(100% + 32px)',
      minWidth: 0,
      maxWidth: 'none',
      maxHeight: '90vh',
      margin: '0 -16px -16px',
      padding: `24px 20px calc(20px + env(safe-area-inset-bottom))`,
      borderRadius: `${theme.radius.xl + 4}px ${theme.radius.xl + 4}px 0 0`,
      animation: `${slideUp} 0.25s ease-out`,
    },
  });

const head =
  (size: 'small' | 'medium') =>
  (theme: Theme): CSSObject => {
    return {
      position: 'relative',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 12,
      marginBottom: size === 'medium' ? '20px' : '12px',
      '> p': {
        flex: 1,
        minWidth: 0,
        color: theme.color.text,
        fontSize: size === 'medium' ? '20px' : '18px',
        fontWeight: 700,
        lineHeight: 1.4,
        paddingTop: 4,
      },
      [mobile]: {
        '> p': {
          fontSize: 18,
        },
      },
    };
  };

const closeButton = (theme: Theme): CSSObject => ({
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  margin: '-2px -6px 0 0',
  borderRadius: theme.radius.md,
  color: theme.color.grey500,
  transition: 'background-color 0.15s ease',
  ':hover': {
    background: theme.color.grey75,
    color: theme.color.text,
  },
});

const contents = (size: 'small' | 'medium') => (): CSSObject => {
  return {
    flex: 1,
    minHeight: 0,
    overflowX: 'auto',
    overflowY: 'auto',
    marginBottom: size === 'medium' ? '24px' : '16px',
    fontSize: 14,
    lineHeight: 1.6,
    wordBreak: 'keep-all',
  };
};

const foot = (theme: Theme): CSSObject => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  '> button': {
    background: theme.color.primary,
    color: theme.color.white,
    height: '52px',
    width: '100%',
    maxWidth: 320,
    borderRadius: theme.radius.md + 2,
    fontSize: 16,
    fontWeight: 600,
    transition: 'background-color 0.15s ease',
    ':hover': {
      background: theme.color.primaryHover,
    },
  },
  [mobile]: {
    '> button': {
      maxWidth: 'none',
    },
  },
});

export { wrap, head, closeButton, contents, foot };
